import { useSyncExternalStore } from 'react'
import {
  ALL_CAPABILITIES,
  CAPABILITY_KEYS,
  PRESET_CAPABILITIES,
  capabilitiesOf,
  type CapabilityMap,
  type Preset,
} from './capabilities'

/**
 * WHAT EACH CLUB CHOSE TO RUN, AS THE READS IN THIS TAB HAVE SEEN IT (D16, ADR §6).
 *
 * There is no capability fetch. The map rides two reads the app already makes, and both write
 * here as they pass — the way `linkLane.ts` and `publicScheduleVisibility.ts` remember their facts:
 *   · the signed-in club read (`GET /organizations/:id`, the workspace's meta wave) — authoritative:
 *     a row without a map IS a legacy club, so it is written as all on;
 *   · every public club read — written only when the row carries a map, since a row that does not
 *     may simply be an older shape and must not overwrite what the club read said.
 * The workspace board for a signed-out visitor has no club read of its own; the public read the
 * page already makes for its publishing gate (`usePublicScheduleHidden`) fills it in.
 *
 * Subscribable, so a save in Club setup → Products (which writes the answer here) redraws every
 * surface at once and nothing has to be re-read. NOTHING SURVIVES A PAGE LOAD.
 */

const known = new Map<string, CapabilityMap>()
const listeners = new Set<() => void>()

const sameMap = (a: CapabilityMap | undefined, b: CapabilityMap) =>
  Boolean(a) && CAPABILITY_KEYS.every((key) => a?.[key] === b[key])

function write(orgId: string, caps: CapabilityMap): void {
  if (!orgId || sameMap(known.get(orgId), caps)) return
  known.set(orgId, caps)
  listeners.forEach((listener) => listener())
}

type CapabilitySource = { orgId?: string; capabilities?: unknown } | null | undefined

/** A club read passing through. `authoritative` = the signed-in club read (see above). */
export function rememberClubCapabilities(org: CapabilitySource, authoritative: boolean): void {
  const orgId = String(org?.orgId || '').trim()
  if (!orgId) return
  if (!authoritative && (org?.capabilities === undefined || org?.capabilities === null)) return
  write(orgId, capabilitiesOf(org))
}

/** The map a fresh club was created with, until its first club read answers for it. */
export function rememberPresetCapabilities(orgId: string, preset: Preset): void {
  write(String(orgId || '').trim(), PRESET_CAPABILITIES[preset])
}

/** The PATCH's answer (Club setup → Products): the full resolved map after the change. */
export function rememberSavedCapabilities(orgId: string, capabilities: unknown): void {
  write(String(orgId || '').trim(), capabilitiesOf({ capabilities }))
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The club's map, or `undefined` while no read has answered for it. */
export function useKnownClubCapabilities(orgId: string): CapabilityMap | undefined {
  const read = () => known.get(String(orgId || '').trim())
  return useSyncExternalStore(subscribe, read, read)
}

/** The club's map — the legacy answer (all on) until a read says otherwise. */
export function useClubCapabilities(orgId: string): CapabilityMap {
  return useKnownClubCapabilities(orgId) ?? ALL_CAPABILITIES
}
